import { useId, useState } from 'react';
import { CalendarClock, FileText, Info, Layers3, Maximize2 } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@helios/ui';
import { useFinanceInvoice } from './financeCommandCenter.hooks';
import { formatRecordedAmount, INVOICE_BUCKETS } from './financeInvoices.types';
import type { FinanceInvoiceRecord, InvoiceDirection } from './financeInvoices.types';
import { FinanceInvoiceEvidenceWorkspace } from './FinanceInvoiceEvidenceWorkspace';
import {
  invoiceDayInterval,
  invoiceDisplayDate,
  outstandingPercentage,
} from './financeInvoiceInsights';
import './finance-invoice-dialog.css';

type View = 'overview' | 'evidence' | 'followups';

export function FinanceInvoiceDialog({
  direction,
  id,
  asOf,
  onClose,
}: {
  direction: InvoiceDirection;
  id: string | null;
  asOf: string;
  onClose: () => void;
}) {
  const query = useFinanceInvoice(direction, id, asOf);
  const invoice =
    !query.isFetching && !query.isError && query.data?.id === id ? query.data : undefined;
  return (
    <Dialog
      open={Boolean(id)}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="finance-invoice-dialog">
        {invoice ? (
          <InvoiceWorkspace
            key={`${direction}:${invoice.id}`}
            invoice={invoice}
            direction={direction}
            asOf={asOf}
          />
        ) : (
          <>
            <DialogHeader className="finance-invoice__header">
              <DialogTitle>
                {direction === 'ar' ? 'Receivable' : 'Payable'} source record
              </DialogTitle>
              <DialogDescription>
                Recorded invoice evidence. Aging date {asOf}; balances reflect the current stored
                record.
              </DialogDescription>
            </DialogHeader>
            <div className="finance-invoice__state">
              {query.isLoading || query.isFetching ? (
                <Skeleton className="h-64 w-full" aria-label="Loading invoice details" />
              ) : (
                <div role="alert" className="space-y-3">
                  <p className="text-sm">
                    {query.error instanceof Error
                      ? query.error.message
                      : 'Invoice details are unavailable with current access.'}
                  </p>
                  <Button size="sm" variant="outline" onClick={() => query.refetch()}>
                    Retry invoice details
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
        <footer className="finance-invoice__footer">
          <p>
            <Info size={14} aria-hidden="true" />
            <span>
              Recorded paid status is not evidence of bank settlement. No payment, collection notice
              or provider action is performed here.
            </span>
          </p>
          <Button size="sm" variant="outline" onClick={onClose}>
            Close source record
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceWorkspace({
  invoice,
  direction,
  asOf,
}: {
  invoice: FinanceInvoiceRecord;
  direction: InvoiceDirection;
  asOf: string;
}) {
  const [view, setView] = useState<View>('overview');
  const [field, setField] = useState<{ label: string; value: string; page: number } | null>(null);
  const prefix = useId();
  const percentage = outstandingPercentage(invoice.amount, invoice.balanceDue);
  const age = invoiceDayInterval(invoice.invoiceDate, asOf);
  const terms = invoiceDayInterval(invoice.invoiceDate, invoice.dueDate);
  const late = invoice.daysPastDue !== null && invoice.daysPastDue > 0;
  const source =
    invoice.sourceSystem === 'quickbooks_online' ? 'QuickBooks Online' : invoice.sourceSystem;
  const metadata = [
    ['Invoice', invoice.invoiceNumber],
    ['Recorded status', invoice.recordedStatus],
    ['Recorded aging tag', invoice.recordedAgingBucket || 'Not recorded'],
    [
      'Project',
      invoice.project
        ? `${invoice.project.projectNumber} · ${invoice.project.name}`
        : 'No accessible project link',
    ],
    ['Source system', source ?? 'Not recorded'],
    ['Source reference', invoice.sourceReference ?? 'Not recorded'],
    ['Source document number', invoice.sourceDocumentNumber ?? 'Not recorded'],
    [
      'Record updated',
      Number.isFinite(Date.parse(invoice.updatedAt))
        ? new Date(invoice.updatedAt).toLocaleString(undefined, { timeZoneName: 'short' })
        : 'Not recorded',
    ],
  ];
  return (
    <>
      <DialogHeader className="finance-invoice__header">
        <div className="finance-invoice__title-line">
          <FileText size={20} aria-hidden="true" />
          <DialogTitle>{direction === 'ar' ? 'Receivable' : 'Payable'} source record</DialogTitle>
          <span className={['finance-invoice__status', late ? 'is-late' : ''].join(' ')}>
            {invoice.recordedStatus.replaceAll('_', ' ')}
          </span>
        </div>
        <DialogDescription className="finance-invoice__subtitle">
          <button
            className="finance-invoice__identity"
            type="button"
            title={invoice.partyName}
            onClick={() =>
              setField({
                label: direction === 'ar' ? 'Customer' : 'Vendor',
                value: invoice.partyName,
                page: 0,
              })
            }
          >
            <strong>{invoice.partyName}</strong>
          </button>
          <span>·</span>
          <button
            className="finance-invoice__identity"
            type="button"
            title={invoice.sourceDocumentNumber || invoice.invoiceNumber}
            onClick={() =>
              setField({
                label: 'Source document number',
                value: invoice.sourceDocumentNumber || invoice.invoiceNumber,
                page: 0,
              })
            }
          >
            {invoice.sourceDocumentNumber || invoice.invoiceNumber}
          </button>
        </DialogDescription>
      </DialogHeader>
      <div className="finance-invoice__tabs" role="tablist" aria-label="Invoice workspace sections">
        {(
          [
            ['overview', 'Overview'],
            ['evidence', 'Source evidence'],
            ['followups', 'Activity & job'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`${prefix}-tab-${key}`}
            aria-selected={view === key}
            aria-controls={`${prefix}-${key}`}
            tabIndex={view === key ? 0 : -1}
            onClick={() => setView(key)}
            onKeyDown={(event) => {
              const keys: View[] = ['overview', 'evidence', 'followups'];
              const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
              if (step || event.key === 'Home' || event.key === 'End') {
                event.preventDefault();
                const next =
                  event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                      ? 2
                      : (keys.indexOf(key) + step + 3) % 3;
                setView(keys[next]!);
                document.getElementById(`${prefix}-tab-${keys[next]}`)?.focus();
              }
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="finance-invoice__body" data-view={view}>
        <section
          id={`${prefix}-overview`}
          aria-label="Invoice balance and aging"
          className="finance-invoice__overview"
          data-panel="overview"
        >
          <div className="finance-invoice__balance-panel">
            <div className="finance-invoice__balance">
              <span className="finance-invoice__label">Outstanding balance</span>
              <strong>{formatRecordedAmount(invoice.balanceDue)}</strong>
              <span className="finance-invoice__muted">Currency not recorded</span>
              <div className="finance-invoice__original">
                <span>Original amount</span>
                <b>{formatRecordedAmount(invoice.amount)}</b>
              </div>
            </div>
            <figure
              className={['finance-invoice__ratio', late ? 'is-late' : ''].join(' ')}
              aria-label={
                percentage === null
                  ? 'Outstanding percentage unavailable'
                  : `${percentage}% outstanding; balance divided by original amount`
              }
            >
              <svg viewBox="0 0 120 120" aria-hidden="true">
                <circle cx="60" cy="60" r="49" className="finance-invoice__ring-track" />
                {percentage !== null && (
                  <circle
                    cx="60"
                    cy="60"
                    r="49"
                    className="finance-invoice__ring-value"
                    pathLength="100"
                    strokeDasharray={`${Math.min(percentage, 100)} 100`}
                    transform="rotate(-90 60 60)"
                  />
                )}
              </svg>
              <div>
                <b>{percentage === null ? '—' : `${percentage}%`}</b>
                <span>outstanding</span>
              </div>
              <figcaption>Balance / original amount</figcaption>
              {percentage !== null && percentage > 100 ? (
                <p className="finance-invoice__muted">Balance exceeds original</p>
              ) : null}
            </figure>
          </div>
          <div className={['finance-invoice__aging', late ? 'is-late' : ''].join(' ')}>
            <div className="finance-invoice__section-title">
              <h3>
                <CalendarClock size={15} aria-hidden="true" /> Due-date aging
              </h3>
              <span>{invoice.bucket ?? 'Unclassified'}</span>
            </div>
            <div className="finance-invoice__aging-summary">
              <strong>{invoice.daysPastDue === null ? 'Unknown' : invoice.daysPastDue}</strong>
              <span>
                {invoice.daysPastDue === null ? 'due date not recorded' : 'days past due'}
              </span>
            </div>
            <div
              className="finance-invoice__ladder"
              role="img"
              aria-label={`Due-date aging: ${invoice.bucket ?? 'unclassified; due date missing'}. Calculated as of ${asOf}.`}
            >
              {INVOICE_BUCKETS.map((bucket) => (
                <div key={bucket} className={invoice.bucket === bucket ? 'is-active' : ''}>
                  <span>{bucket}</span>
                  <i />
                </div>
              ))}
            </div>
            <dl className="finance-invoice__dates">
              <div>
                <dt>Invoice date</dt>
                <dd>{invoiceDisplayDate(invoice.invoiceDate)}</dd>
              </div>
              <div>
                <dt>Due date</dt>
                <dd>{invoiceDisplayDate(invoice.dueDate)}</dd>
              </div>
              <div>
                <dt>Aging date</dt>
                <dd>{invoiceDisplayDate(asOf)}</dd>
              </div>
            </dl>
          </div>
          <div className="finance-invoice__context">
            <div>
              <span>Invoice age</span>
              <b>{age === null ? 'Unavailable' : `${age} days`}</b>
            </div>
            <div>
              <span>Invoice-to-due interval</span>
              <b>{terms === null ? 'Unavailable' : `${terms} days`}</b>
            </div>
          </div>
        </section>
        <section
          id={`${prefix}-evidence`}
          aria-label="Invoice source evidence"
          className="finance-invoice__evidence"
          data-panel="evidence"
        >
          <h3>
            <Layers3 size={15} aria-hidden="true" /> Source evidence
          </h3>
          <dl>
            {metadata.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>
                  <button
                    type="button"
                    className="finance-invoice__field"
                    aria-label={`View full ${label}`}
                    title={value}
                    onClick={() => setField({ label: label!, value: value!, page: 0 })}
                  >
                    <span>{value}</span>
                    <Maximize2 size={11} aria-hidden="true" />
                  </button>
                </dd>
              </div>
            ))}
          </dl>
        </section>
        <aside
          id={`${prefix}-followups`}
          aria-label="Invoice evidence workspace"
          className="finance-invoice__followups"
          data-panel="followups"
        >
          <FinanceInvoiceEvidenceWorkspace direction={direction} invoice={invoice} asOf={asOf} />
        </aside>
      </div>
      <Dialog
        open={field !== null}
        onOpenChange={(open) => {
          if (!open) setField(null);
        }}
      >
        <DialogContent className="finance-invoice__field-dialog">
          <DialogHeader>
            <DialogTitle>{field?.label}</DialogTitle>
            <DialogDescription>
              Exact recorded value · text is paged to fit this window.
            </DialogDescription>
          </DialogHeader>
          <p className="finance-invoice__field-value">
            {field?.value.slice(field.page * 240, (field.page + 1) * 240)}
          </p>
          <div className="finance-invoice__field-pages">
            <Button
              size="xs"
              variant="outline"
              disabled={!field || field.page === 0}
              onClick={() => setField((value) => value && { ...value, page: value.page - 1 })}
            >
              Previous value page
            </Button>
            <span>
              {field
                ? `${field.page + 1} / ${Math.max(1, Math.ceil(field.value.length / 240))}`
                : ''}
            </span>
            <Button
              size="xs"
              variant="outline"
              disabled={!field || (field.page + 1) * 240 >= field.value.length}
              onClick={() => setField((value) => value && { ...value, page: value.page + 1 })}
            >
              Next value page
            </Button>
          </div>
          <Button size="sm" variant="outline" onClick={() => setField(null)}>
            Close recorded value
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
